import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../../services/api';
import { DailySearchChart, fmtDay } from './DailySearchChart';

const Stat = ({ label, value, tone = 'text-white', testid }) => (
  <div className="rounded-lg bg-gray-800/80 p-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`text-xl font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</p>
    {children}
  </div>
);

export const TermDetailSheet = ({ term, range, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!term) return;
    setData(null); setError(null);
    api.get('/analytics/searches/term', {
      params: { term, start: range.start, end: range.end },
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    }).then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || 'No se pudo cargar'));
  }, [term, range.start, range.end]);

  return (
    <Sheet open={!!term} onOpenChange={o => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-gray-900 border-gray-800 text-white overflow-y-auto" data-testid="term-detail-sheet">
        <SheetHeader>
          <SheetTitle className="text-white text-2xl" data-testid="term-detail-title">“{term}”</SheetTitle>
          <SheetDescription className="text-gray-400">
            {fmtDay(range.start, { day: 'numeric', month: 'short', year: 'numeric' })} – {fmtDay(range.end, { day: 'numeric', month: 'short', year: 'numeric' })}
          </SheetDescription>
        </SheetHeader>
        {error ? <p className="text-rose-400 mt-6" data-testid="term-detail-error">{error}</p> : !data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="space-y-6 mt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Búsquedas" value={data.count} tone="text-orange-400" testid="term-stat-count" />
              <Stat label="Histórico" value={data.all_time_count} testid="term-stat-all-time" />
              <Stat label="Prom. resultados" value={data.avg_results} tone={data.avg_results === 0 ? 'text-rose-400' : 'text-white'} testid="term-stat-avg" />
              <Stat label="Sin resultados" value={`${data.zero_results_pct}%`} tone={data.zero_results ? 'text-rose-400' : 'text-white'} testid="term-stat-zero" />
              <Stat label="Clics a producto" value={data.clicks} testid="term-stat-clicks" />
              <Stat label="CTR" value={`${data.ctr_pct}%`} testid="term-stat-ctr" />
              <Stat label="Usuarios únicos" value={data.unique_users} testid="term-stat-users" />
              <Stat label="Rango resultados" value={`${data.min_results}–${data.max_results}`} testid="term-stat-range" />
            </div>
            <p className="text-xs text-gray-500">
              Primera búsqueda: {data.first_searched ? new Date(data.first_searched).toLocaleString('es-MX') : '—'} · Última: {data.last_searched ? new Date(data.last_searched).toLocaleString('es-MX') : '—'}
            </p>
            <Section title="Búsquedas por día">
              <DailySearchChart data={data.by_day} height={200} testid="term-daily-chart" />
            </Section>
            <Section title="Horario (UTC)">
              <div className="h-32" data-testid="term-hour-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_hour} margin={{ left: -30, right: 0 }}>
                    <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} interval={2} tickFormatter={h => `${h}h`} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#030712', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }} labelFormatter={h => `${h}:00 – ${h}:59`} formatter={v => [v, 'Búsquedas']} />
                    <Bar dataKey="count" fill="#38bdf8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
            <Section title="Productos que abrieron desde esta búsqueda">
              {data.clicked_products.length === 0 ? <p className="text-sm text-gray-500">Aún sin clics registrados.</p> : (
                <ul className="space-y-1" data-testid="term-clicked-products">
                  {data.clicked_products.map(p => (
                    <li key={p.product_id} className="flex justify-between text-sm bg-gray-800/60 rounded px-3 py-2">
                      <Link to={`/product/${p.product_id}`} target="_blank" className="text-gray-200 hover:text-orange-300 truncate">{p.title}</Link>
                      <span className="text-orange-400 font-semibold">{p.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Búsquedas recientes">
              <ul className="divide-y divide-gray-800" data-testid="term-recent">
                {data.recent.map((r, i) => (
                  <li key={i} className="flex justify-between py-2 text-sm">
                    <span className="text-gray-300">{r.date ? new Date(r.date).toLocaleString('es-MX') : '—'}</span>
                    <span className="text-gray-500">{r.user || 'Invitado'}</span>
                    <span className={r.results_count === 0 ? 'text-rose-400' : 'text-gray-300'}>{r.results_count} resultados</span>
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
